import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import {
  useApi,
  configApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  GroupEntity,
  UserEntity,
  RELATION_HAS_MEMBER,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { TeamNameField } from '../fields/TeamNameField/TeamNameField';
import { MachineIdDisplay } from '../fields/MachineIdDisplay/MachineIdDisplay';
import { ParentGroupPicker } from '../fields/ParentGroupPicker/ParentGroupPicker';
import {
  FourInTheBoxSection,
  FourInTheBoxValues,
} from '../fields/FourInTheBoxSection/FourInTheBoxSection';
import { MembersPicker } from '../fields/MembersPicker/MembersPicker';
import { MemberTitleSelect } from '../fields/MemberTitleSelect/MemberTitleSelect';
import { MemberTitle } from '../../utils/memberTitles';
import {
  ResourceLinkEditor,
  ResourceLink,
  hasResourceLinkErrors,
} from '../fields/ResourceLinkEditor/ResourceLinkEditor';
import { OwnsAssetsToggle } from '../fields/OwnsAssetsToggle/OwnsAssetsToggle';
import { WorksOnBehalfOfPicker } from '../fields/WorksOnBehalfOfPicker/WorksOnBehalfOfPicker';
import { groupLevelLabel } from '../../utils/groupLevelLabel';
import { useWoboContext, type WoboContext } from '../../hooks/useWoboContext';
import {
  DEFAULT_NAMESPACE,
  groupRefOf,
  resolveUserGroupRefs,
} from '@internal/backstage-catalog-utils';

// Must stay in sync with MAX_MISSION_LENGTH in
// plugins/team-management-backend/src/github/yaml.ts, which is what actually
// enforces the limit on POST /teams, PUT /teams/:machineId and the
// team:write-yaml action. Duplicated rather than imported because the frontend
// must not depend on the backend package (see plugins/team-management-frontend/src/utils/machineId.ts for the
// same precedent).
const MAX_MISSION_LENGTH = 1000;

const useStyles = makeStyles(theme => ({
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface TeamEditFormProps {
  entity: GroupEntity;
}

interface PrimaryTeamPromptTeam {
  ref: string;
  title: string;
}

interface PrimaryTeamPromptState {
  memberRef: string;
  memberDisplayName: string;
  teams: PrimaryTeamPromptTeam[];
  currentPrimaryRef?: string;
  selected?: string;
}

// Removing someone from their current primary team, while they still
// belong to 2+ OTHER teams, leaves a real decision unmade — the primary
// they had is gone either way, so the editor must immediately pick which
// of the remaining teams takes over, rather than leaving them silently
// primary-less until someone remembers to click a star later. Mandatory,
// same as the add-side prompt — neither has a way to dismiss without
// choosing.
interface RemovalPrimaryPromptState {
  memberRef: string;
  memberDisplayName: string;
  teams: PrimaryTeamPromptTeam[];
  selected?: string;
}

function parseAnnotation(entity: GroupEntity, key: string): string | undefined {
  return entity.metadata.annotations?.[key];
}

// spec.worksOnBehalfOf is always written as a bare machine ID by this
// plugin's own write path (WorksOnBehalfOfPicker only ever emits one), but
// EntityWorksOnBehalfOfCard defensively tolerates a fully-qualified ref
// (e.g. "group:default/platform-xp") too, in case one ever ends up stored
// some other way. Mirror that same tolerance here: WorksOnBehalfOfPicker's
// `value` prop expects a bare machine ID, and if a qualified ref were kept
// as-is, saving the form would resubmit it unchanged — the backend's
// getGroupByMachineId builds "group:default/<value>" itself, so a
// double-qualified ref would fail to resolve and the edit would break.
function normalizeWorksOnBehalfOf(raw: string | undefined): string | undefined {
  if (!raw) return raw;
  return raw.includes(':') ? raw.split('/').pop() : raw;
}

function teamsBelow(count: number) {
  return `${count} ${count === 1 ? 'team' : 'teams'}`;
}

function WoboInheritanceNotes({
  context,
  hasValue,
  hadValue,
}: {
  context: WoboContext;
  hasValue: boolean;
  hadValue: boolean;
}) {
  const { loading, own, inherited, inheritingTeamCount } = context;
  if (loading) return null;

  const route = inherited && (
    <>
      {inherited.from.title} → {inherited.target.title}
    </>
  );

  return (
    <>
      {!hasValue && inherited && (
        <Box mt={1}>
          <Typography variant="body2" color="textSecondary">
            Inherited from {route} (leave empty to keep inheriting)
          </Typography>
        </Box>
      )}
      {hasValue &&
        inherited &&
        own.kind === 'explicit' &&
        own.targetRef !== inherited.target.ref && (
          <Box mt={1}>
            <Typography variant="body2" color="textSecondary">
              Overrides inherited {route}
            </Typography>
          </Box>
        )}
      {hasValue && inheritingTeamCount > 0 && (
        <Box mt={1}>
          <Alert severity="info">
            Applies to {teamsBelow(inheritingTeamCount)} below that don't set
            their own
          </Alert>
        </Box>
      )}
      {!hasValue && hadValue && inheritingTeamCount > 0 && (
        <Box mt={1}>
          <Alert severity="warning">
            Removing this stops {teamsBelow(inheritingTeamCount)} below from
            inheriting it
          </Alert>
        </Box>
      )}
    </>
  );
}

// Upper bound on the ?addMember= catalog lookup before Save is re-enabled.
const DEEP_LINK_LOOKUP_TIMEOUT_MS = 10_000;

export function TeamEditForm({ entity }: TeamEditFormProps) {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const addMemberParam = searchParams.get('addMember');

  const machineId = entity.metadata.name;
  const backendBase = `${configApi.getString('backend.baseUrl')}/api/team-management`;
  // PAT-771: Owns Assets is leaf-team-only — function/sub-function/xp
  // entities are now editable through this same form, but must never see
  // this toggle at all (the backend rejects it server-side too).
  const level = entity.metadata.annotations?.['team/level'] ?? 'team';
  const isTeamLevel = level === 'team';
  // This same form edits the function / sub-function / XP levels too, so the
  // level-named copy ("Function Name", …) has to follow the entity's level
  // instead of always saying "Team".
  const levelLabel = groupLevelLabel(level);

  // New format: entity refs stored directly in team/lead, team/pm, etc.
  const leadRef = parseAnnotation(entity, 'team/lead');
  const pmRef = parseAnnotation(entity, 'team/pm');
  const techLeadRef = parseAnnotation(entity, 'team/tech-lead');
  const architectRef = parseAnnotation(entity, 'team/architect');

  // Legacy format: email annotations needing catalog lookup
  const leadEmail = !leadRef
    ? parseAnnotation(entity, 'team/lead-email')
    : undefined;
  const pmEmail = !pmRef ? parseAnnotation(entity, 'team/pm-email') : undefined;
  const techLeadEmail = !techLeadRef
    ? parseAnnotation(entity, 'team/tech-lead-email')
    : undefined;
  const architectEmail = !architectRef
    ? parseAnnotation(entity, 'team/architect-email')
    : undefined;

  // Initialise form state from entity
  const [teamName, setTeamName] = useState(entity.metadata.title ?? machineId);
  const [parent, setParent] = useState<string>(
    String(entity.spec?.parent ?? ''),
  );
  const [mission, setMission] = useState(entity.metadata.description ?? '');
  const [leadership, setLeadership] = useState<FourInTheBoxValues>({
    lead: leadRef,
    pm: pmRef,
    techLead: techLeadRef,
    architect: architectRef,
  });

  // Resolve legacy email annotations → entity refs (only needed for old-format files)
  useEffect(() => {
    const emails = [leadEmail, pmEmail, techLeadEmail, architectEmail].filter(
      (e): e is string => Boolean(e),
    );
    if (emails.length === 0) return;

    catalogApi
      .getEntities({
        filter: { kind: 'User' },
        fields: ['metadata.name', 'spec.profile'],
      })
      .then(res => {
        const emailToRef = new Map<string, string>();
        for (const e of res.items) {
          const u = e as UserEntity;
          const email = u.spec?.profile?.email;
          if (email) emailToRef.set(email, `user:default/${u.metadata.name}`);
        }
        setLeadership(prev => ({
          lead: leadEmail
            ? (emailToRef.get(leadEmail) ?? prev.lead)
            : prev.lead,
          pm: pmEmail ? (emailToRef.get(pmEmail) ?? prev.pm) : prev.pm,
          techLead: techLeadEmail
            ? (emailToRef.get(techLeadEmail) ?? prev.techLead)
            : prev.techLead,
          architect: architectEmail
            ? (emailToRef.get(architectEmail) ?? prev.architect)
            : prev.architect,
        }));
      })
      .catch(() => {});
  }, [catalogApi, leadEmail, pmEmail, techLeadEmail, architectEmail]); // eslint-disable-line react-hooks/exhaustive-deps
  const [members, setMembers] = useState<string[]>(() =>
    (entity.relations ?? [])
      .filter(
        r =>
          r.type === RELATION_HAS_MEMBER &&
          r.targetRef.toLowerCase().startsWith('user:'),
      )
      .map(r => r.targetRef),
  );
  const [memberTitles, setMemberTitles] = useState<Record<string, MemberTitle>>(
    (entity.spec as { memberTitles?: Record<string, MemberTitle> } | undefined)
      ?.memberTitles ?? {},
  );
  const [links, setLinks] = useState<ResourceLink[]>(
    (
      (entity.metadata.links ?? []) as Array<{
        url: string;
        title?: string;
        icon?: string;
        type?: string;
        channelId?: string;
      }>
    ).map(l => {
      const type = (
        ['slack', 'jira', 'confluence', 'figma'].includes(l.icon ?? '')
          ? l.icon
          : 'custom'
      ) as ResourceLink['type'];
      return {
        type,
        label: l.title ?? '',
        url: l.url,
        slackSubType:
          type === 'slack'
            ? (l.type as ResourceLink['slackSubType'])
            : undefined,
        channelId: type === 'slack' ? l.channelId : undefined,
      };
    }),
  );
  const [ownsAssets, setOwnsAssets] = useState(
    parseAnnotation(entity, 'team/owns-assets') === 'true',
  );
  const [worksOnBehalfOf, setWorksOnBehalfOf] = useState<string | undefined>(
    normalizeWorksOnBehalfOf(
      (entity.spec as { worksOnBehalfOf?: string } | undefined)
        ?.worksOnBehalfOf,
    ),
  );

  // PAT-1066: previews inheritance from the unsaved parent/WOBO values.
  const rawOriginalWobo = (
    entity.spec as { worksOnBehalfOf?: string } | undefined
  )?.worksOnBehalfOf;
  const originalWobo = normalizeWorksOnBehalfOf(rawOriginalWobo);
  const woboContext = useWoboContext(entity, {
    parent,
    worksOnBehalfOf:
      worksOnBehalfOf === originalWobo ? rawOriginalWobo : worksOnBehalfOf,
  });
  const [submitting, setSubmitting] = useState(false);
  const [missionFocused, setMissionFocused] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [serverError, setServerError] = useState<string | undefined>();

  // Primary team (PAT-678, lead-assigned v1): maps each member ref to their
  // currently-stored user/primary-team annotation value (a group ref like
  // "group:default/platform"), so the star icon reflects whether THIS team
  // is that member's primary team. Canonicalized via the shared groupRefOf —
  // resolveUserGroupRefs below returns namespace-aware canonical refs, so a
  // hardcoded "group:default/..." here would fail to exclude this team from
  // its own "other teams" list for a team outside the default namespace.
  const thisTeamRef = groupRefOf(entity);
  const [primaryTeamByMember, setPrimaryTeamByMember] = useState<
    Map<string, string>
  >(new Map());
  const [settingPrimaryFor, setSettingPrimaryFor] = useState<
    string | undefined
  >();
  const [primaryTeamError, setPrimaryTeamError] = useState<
    string | undefined
  >();

  // Primary-team prompt on add (PAT-779): when a member added via
  // MembersPicker already belongs to another team, ask the editor which
  // team should now be primary instead of leaving the decision unmade.
  const [primaryPrompt, setPrimaryPrompt] = useState<
    PrimaryTeamPromptState | undefined
  >();
  // ANY choice made in the add-side dialog is deferred until this form's
  // own save succeeds — not just "pick this team", but "pick an existing
  // OTHER team" too. The whole reason the dialog appeared is the pending,
  // not-yet-saved membership change: if the editor picks an existing team
  // and then navigates away without saving, that add never happens, and
  // writing the primary immediately would leave the person with a primary
  // team while genuinely belonging to only one team — violating the "a
  // primary only exists with 2+ teams" invariant this whole feature is
  // built around. The removal-side dialog defers for a different reason:
  // not because its own choice is unsafe (the remaining teams offered there
  // are already-real, currently-persisted memberships, independent of
  // whether the pending removal from THIS team ever saves), but so an
  // abandoned edit — never clicking Save — can't cause a persisted
  // side-effect on its own. A list, not a single slot, since an editor can
  // trigger both an add-dialog and a removal-dialog assignment for
  // different people in the same unsaved session.
  const [pendingPrimaryAssignments, setPendingPrimaryAssignments] = useState<
    { memberRef: string; teamRef: string }[]
  >([]);

  // Mandatory reassignment prompt on remove: when someone whose primary IS
  // this team gets removed, and they still belong to 2+ OTHER teams.
  const [removalPrimaryPrompt, setRemovalPrimaryPrompt] = useState<
    RemovalPrimaryPromptState | undefined
  >();

  useEffect(() => {
    const leadershipRefs = Object.values(leadership).filter((r): r is string =>
      Boolean(r),
    );
    const refsToResolve = Array.from(new Set([...members, ...leadershipRefs]));
    if (refsToResolve.length === 0) {
      setPrimaryTeamByMember(new Map());
      return;
    }
    catalogApi
      .getEntitiesByRefs({
        entityRefs: refsToResolve,
        fields: ['metadata.name', 'metadata.annotations'],
      })
      .then(({ items }) => {
        const map = new Map<string, string>();
        items.forEach((e, i) => {
          const primary = e?.metadata.annotations?.['user/primary-team'];
          if (primary) map.set(refsToResolve[i], primary);
        });
        setPrimaryTeamByMember(map);
      })
      .catch(() => {});
  }, [catalogApi, members, leadership]);

  // Shared by the star-toggle (always targets this team) and the add-member
  // prompt (can target this team or any other team the member already
  // belongs to) — `teamRef` determines which team's endpoint is called.
  // Always passes viaTeamId (this team, which the editor is already
  // authorized to edit) so the backend can authorize setting primary on an
  // EXISTING other team the editor isn't 4-in-the-box for — otherwise a
  // valid add-dialog choice could 403 and leave the person with no primary
  // at all despite the dialog being mandatory.
  const setPrimaryTeamFor = async (
    memberRef: string,
    teamRef: string,
  ): Promise<{ ok: true } | { ok: false; error: string }> => {
    const target = parseEntityRef(teamRef, { defaultKind: 'group' });
    const editedNamespace = parseEntityRef(thisTeamRef, {
      defaultKind: 'group',
    }).namespace;
    if (
      target.namespace !== DEFAULT_NAMESPACE ||
      editedNamespace !== DEFAULT_NAMESPACE
    ) {
      return {
        ok: false,
        error: `Setting a primary team is only supported when both teams are in the "${DEFAULT_NAMESPACE}" namespace (edited team: ${thisTeamRef}; target team: ${teamRef})`,
      };
    }
    const targetMachineId = target.name;
    try {
      const response = await fetchApi.fetch(
        `${backendBase}/teams/${targetMachineId}/primary-team`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEntityRef: memberRef,
            viaTeamId: machineId,
          }),
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const err = data.error;
        return {
          ok: false,
          error:
            typeof err === 'string'
              ? err
              : (err?.message ?? 'Failed to set primary team'),
        };
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: String(err) };
    }
  };

  const handleSetPrimary = async (memberRef: string) => {
    setSettingPrimaryFor(memberRef);
    setPrimaryTeamError(undefined);
    const result = await setPrimaryTeamFor(memberRef, thisTeamRef);
    if (!result.ok) {
      setPrimaryTeamError(result.error);
    } else {
      setPrimaryTeamByMember(prev => {
        const next = new Map(prev);
        next.set(memberRef, thisTeamRef);
        return next;
      });
    }
    setSettingPrimaryFor(undefined);
  };

  // Shared star-toggle action, rendered per-row on both MembersPicker and
  // FourInTheBoxSection — role (plain member vs. EM/PM/Tech Lead/Architect)
  // doesn't matter for who can be marked primary, only team membership does.
  const renderPrimaryStarAction = (ref: string) => {
    const isPrimary = primaryTeamByMember.get(ref) === thisTeamRef;
    const isSettingThis = settingPrimaryFor === ref;

    let icon: JSX.Element;
    if (isSettingThis) {
      icon = <CircularProgress size={16} />;
    } else if (isPrimary) {
      icon = <StarIcon fontSize="small" color="primary" />;
    } else {
      icon = <StarBorderIcon fontSize="small" />;
    }

    return (
      <Tooltip
        title={
          isPrimary
            ? 'Primary team for this member'
            : 'Mark this team as primary'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => handleSetPrimary(ref)}
            disabled={isPrimary || isSettingThis}
            aria-label={
              isPrimary ? `${ref} primary team` : `Set primary team for ${ref}`
            }
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  // PAT-857: per-row Designer/Project Manager/Analyst tag selector, rendered
  // alongside the star on the plain-members MembersPicker only — leadership
  // (FourInTheBoxSection) never gets a tag.
  const renderMemberTagAction = (ref: string) => (
    <MemberTitleSelect
      value={memberTitles[ref]}
      ariaLabel={`Tag for ${ref.split('/').pop() ?? ref}`}
      onChange={title =>
        setMemberTitles(prev => {
          const next = { ...prev };
          if (title) next[ref] = title;
          else delete next[ref];
          return next;
        })
      }
    />
  );

  // PAT-857: tags are for regular members only — a member promoted into
  // leadership must not keep a hidden tag.
  const handleLeadershipChange = (values: FourInTheBoxValues) => {
    setLeadership(values);
    const leadershipRefs = Object.values(values).filter((r): r is string =>
      Boolean(r),
    );
    setMemberTitles(prev =>
      Object.fromEntries(
        Object.entries(prev).filter(([ref]) => !leadershipRefs.includes(ref)),
      ),
    );
  };

  // Latest form state, so async continuations (the deep-link lookup and
  // handleMemberAdded) check what the editor has done since they started.
  const membersRef = useRef(members);
  membersRef.current = members;
  const leadershipRef = useRef(leadership);
  leadershipRef.current = leadership;

  // PAT-779: fired when a member is added via MembersPicker. If they already
  // belong to at least one other team, prompt the editor to choose which
  // team is now primary — otherwise the decision would silently never get
  // made until someone remembers to click the star.
  const handleMemberAdded = async (ref: string) => {
    try {
      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: [ref],
        fields: [
          'metadata.name',
          'metadata.annotations',
          'spec.profile',
          'relations',
        ],
      });
      const userEntity = items[0] as UserEntity | undefined;
      if (!userEntity) return;

      // Counts leadership-only roles too, not just spec.members-derived
      // relations — the catalog never creates a relation for a lead-only
      // assignment, so relying on relations() alone would miss any OTHER
      // team this person merely leads.
      const otherTeamRefs = (
        await resolveUserGroupRefs(ref, catalogApi)
      ).filter(r => r !== thisTeamRef);
      // Brand-new member with no other team memberships — nothing to decide.
      if (otherTeamRefs.length === 0) return;

      const currentPrimaryRef =
        userEntity.metadata.annotations?.['user/primary-team'];
      const displayName =
        userEntity.spec?.profile?.displayName ?? ref.split('/').pop() ?? ref;

      const { items: teamEntities } = await catalogApi.getEntitiesByRefs({
        entityRefs: otherTeamRefs,
        fields: ['metadata.name', 'metadata.title'],
      });
      const otherTeams: PrimaryTeamPromptTeam[] = otherTeamRefs.map(
        (teamRef, i) => ({
          ref: teamRef,
          title:
            teamEntities[i]?.metadata.title ??
            teamEntities[i]?.metadata.name ??
            teamRef.split('/').pop() ??
            teamRef,
        }),
      );

      // Only pre-select currentPrimaryRef if it's actually one of the
      // offered options — a stale annotation pointing at a team the person
      // no longer belongs to would otherwise leave `selected` set to a ref
      // with no matching radio (visually nothing checked), while the
      // "Set as primary" button — gated only on `selected` being truthy —
      // stays enabled and would enqueue that same invalid ref as primary.
      const validRefs = new Set([thisTeamRef, ...otherTeamRefs]);
      const preselected =
        currentPrimaryRef && validRefs.has(currentPrimaryRef)
          ? currentPrimaryRef
          : undefined;

      // The lookups above are async: if the editor removed this person in the
      // meantime, there is no unsaved membership left to prompt about.
      const stillPresent =
        membersRef.current.some(m => m.toLowerCase() === ref.toLowerCase()) ||
        Object.values(leadershipRef.current).some(
          l => l?.toLowerCase() === ref.toLowerCase(),
        );
      if (!stillPresent) return;

      setPrimaryPrompt({
        memberRef: ref,
        memberDisplayName: displayName,
        teams: [{ ref: thisTeamRef, title: teamName }, ...otherTeams],
        currentPrimaryRef,
        selected: preselected,
      });
    } catch {
      // Best-effort: the membership itself was already added locally above;
      // a failed lookup here just means the editor won't be prompted this
      // time, not that the add failed.
    }
  };

  // PAT-565: ?addMember=<user ref> (deep link from the orphan Slack DM)
  // pre-adds that user as an unsaved member; the editor still has to Save.
  const [preAdded, setPreAdded] = useState<{ ref: string; name: string }>();
  const [deepLinkWarning, setDeepLinkWarning] = useState<string>();
  const [deepLinkInfo, setDeepLinkInfo] = useState<string>();
  const [deepLinkPending, setDeepLinkPending] = useState(false);
  const handledAddMember = useRef<string>();
  useEffect(() => {
    // An empty `?addMember=` is present-but-invalid, so it must reach
    // validation rather than being treated like an absent parameter.
    if (addMemberParam === null || handledAddMember.current === addMemberParam)
      return;
    handledAddMember.current = addMemberParam;
    let ref: string;
    try {
      const parsed = parseEntityRef(addMemberParam, {
        defaultKind: 'user',
        defaultNamespace: DEFAULT_NAMESPACE,
      });
      if (parsed.kind.toLowerCase() !== 'user') throw new Error('not a user');
      ref = stringifyEntityRef(parsed).toLowerCase();
    } catch {
      setDeepLinkWarning(`Ignored invalid member "${addMemberParam}".`);
      return;
    }
    setDeepLinkPending(true);
    // A stalled lookup must not leave Save disabled forever: after the
    // timeout we give up, warn, and ignore any late result.
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      setDeepLinkPending(false);
      setDeepLinkWarning(`Could not look up user "${addMemberParam}".`);
    }, DEEP_LINK_LOOKUP_TIMEOUT_MS);
    catalogApi
      .getEntityByRef(ref)
      .then(async user => {
        if (timedOut) return;
        if (!user) {
          setDeepLinkWarning(`Ignored unknown user "${addMemberParam}".`);
          return;
        }
        const name =
          (user as UserEntity).spec?.profile?.displayName ?? user.metadata.name;
        const alreadyMember =
          membersRef.current.some(m => m.toLowerCase() === ref) ||
          Object.values(leadershipRef.current).some(
            l => l?.toLowerCase() === ref,
          );
        if (alreadyMember) {
          setDeepLinkInfo(`${name} is already on this team.`);
          return;
        }
        setMembers(prev =>
          prev.some(m => m.toLowerCase() === ref) ? prev : [...prev, ref],
        );
        setPreAdded({ ref, name });
        await handleMemberAdded(ref);
      })
      .catch(() => {
        if (!timedOut)
          setDeepLinkWarning(`Could not look up user "${addMemberParam}".`);
      })
      .finally(() => {
        clearTimeout(timer);
        if (!timedOut) setDeepLinkPending(false);
      });
  }, [addMemberParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmPrimaryPrompt = () => {
    if (!primaryPrompt?.selected) return;
    const { memberRef, selected } = primaryPrompt;

    // Deferred until this form's own save succeeds, regardless of which
    // team was picked — see pendingPrimaryAssignments' docstring above.
    setPrimaryTeamByMember(prev => {
      const next = new Map(prev);
      next.set(memberRef, selected);
      return next;
    });
    setPendingPrimaryAssignments(prev => [
      ...prev.filter(a => a.memberRef !== memberRef),
      { memberRef, teamRef: selected },
    ]);
    setPrimaryPrompt(undefined);
  };

  // Fired when a member/leader is removed. If this team was their primary
  // AND they still belong to 2+ other teams, force an immediate replacement
  // pick — the primary they had is gone either way, so leaving it silently
  // unset would be strictly worse than asking.
  const handleMemberRemoved = async (ref: string) => {
    if (pendingPrimaryAssignments.some(a => a.memberRef === ref)) {
      // Undoing the very add that produced this pending assignment — cancel
      // it. There's nothing real to reassign: the primary choice was never
      // saved, since the add it depended on is now itself being undone.
      setPendingPrimaryAssignments(prev =>
        prev.filter(a => a.memberRef !== ref),
      );
      setPrimaryTeamByMember(prev => {
        const next = new Map(prev);
        next.delete(ref);
        return next;
      });
      return;
    }
    if (primaryTeamByMember.get(ref) !== thisTeamRef) return;
    try {
      const remainingRefs = (
        await resolveUserGroupRefs(ref, catalogApi)
      ).filter(r => r !== thisTeamRef);
      // 0 remaining: nothing to reassign to — the save's own reconciliation
      // clears the primary. 1 remaining: unambiguous, but option B never
      // auto-assigns even then — clearing alone is the accepted outcome.
      if (remainingRefs.length < 2) return;

      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: [ref],
        fields: ['spec.profile'],
      });
      const displayName =
        (items[0] as UserEntity | undefined)?.spec?.profile?.displayName ??
        ref.split('/').pop() ??
        ref;

      const { items: teamEntities } = await catalogApi.getEntitiesByRefs({
        entityRefs: remainingRefs,
        fields: ['metadata.name', 'metadata.title'],
      });
      const teams: PrimaryTeamPromptTeam[] = remainingRefs.map(
        (teamRef, i) => ({
          ref: teamRef,
          title:
            teamEntities[i]?.metadata.title ??
            teamEntities[i]?.metadata.name ??
            teamRef.split('/').pop() ??
            teamRef,
        }),
      );

      setRemovalPrimaryPrompt({
        memberRef: ref,
        memberDisplayName: displayName,
        teams,
      });
    } catch {
      // Best-effort: the removal itself already happened locally above; a
      // failed lookup here just means the editor won't be re-prompted this
      // time, and the save's own reconciliation will still clear the stale
      // primary regardless.
    }
  };

  // Deferred until Save, same as the add-side dialog — not because this
  // choice is itself unsafe (the offered teams are already-real, currently-
  // persisted memberships, so the "primary needs 2+ teams" invariant can't
  // break here even if the pending removal from THIS team never saves), but
  // so backing out of an edit without saving can't still leave a persisted
  // side-effect behind.
  const confirmRemovalPrimaryPrompt = () => {
    if (!removalPrimaryPrompt?.selected) return;
    const { memberRef, selected } = removalPrimaryPrompt;

    setPrimaryTeamByMember(prev => {
      const next = new Map(prev);
      next.set(memberRef, selected);
      return next;
    });
    setPendingPrimaryAssignments(prev => [
      ...prev.filter(a => a.memberRef !== memberRef),
      { memberRef, teamRef: selected },
    ]);
    setRemovalPrimaryPrompt(undefined);
  };

  // Parent change warning (FR-027)
  const originalParent = String(entity.spec?.parent ?? '');
  const [showParentWarning, setShowParentWarning] = useState(false);
  const [pendingParent, setPendingParent] = useState<string | undefined>();

  const handleParentChange = (newParent: string) => {
    if (newParent !== originalParent) {
      setPendingParent(newParent);
      setShowParentWarning(true);
    } else {
      setParent(newParent);
    }
  };

  const confirmParentChange = () => {
    if (pendingParent !== undefined) setParent(pendingParent);
    setShowParentWarning(false);
    setPendingParent(undefined);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setServerError(undefined);

    try {
      const profile = await identityApi.getProfileInfo();
      const backstageIdentity = await identityApi.getBackstageIdentity();

      const body = {
        title: teamName,
        description: mission || undefined,
        parent,
        lead: leadership.lead,
        pm: leadership.pm,
        techLead: leadership.techLead,
        architect: leadership.architect,
        members,
        memberTitles,
        links,
        // Force false for non-team levels regardless of the (hidden)
        // toggle's state — otherwise a pre-existing malformed true on a
        // non-team entity would resubmit on every save and permanently
        // block even unrelated edits, since the backend now rejects it
        // unconditionally with no visible control to clear it.
        ownsAssets: isTeamLevel ? ownsAssets : false,
        worksOnBehalfOf,
        actorEntityRef: backstageIdentity.userEntityRef,
        actorEmail: profile.email ?? '',
      };

      const response = await fetchApi.fetch(
        `${backendBase}/teams/${machineId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );

      if (response.status === 403) {
        setServerError('Not authorised to edit this team.');
        return;
      }
      if (!response.ok) {
        const data = await response.json();
        const err = data.error;
        setServerError(
          typeof err === 'string' ? err : (err?.message ?? 'An error occurred'),
        );
        return;
      }

      if (pendingPrimaryAssignments.length > 0) {
        // The membership/removal just persisted above, so each pending
        // assignment is now safe to write — processed one at a time so a
        // failure partway through doesn't re-attempt the ones that already
        // succeeded on a later Save click.
        const remaining = [...pendingPrimaryAssignments];
        while (remaining.length > 0) {
          // Peek, don't remove yet — only drop it from the list once the
          // write actually succeeds, or a failure here would be silently
          // lost instead of staying queued for a retry on the next Save.
          const assignment = remaining[0];
          const result = await setPrimaryTeamFor(
            assignment.memberRef,
            assignment.teamRef,
          );
          if (!result.ok) {
            setPendingPrimaryAssignments(remaining);
            // Don't show the success view here — it would silently bury
            // this error, since it replaces the entire form (including the
            // primaryTeamError banner) the moment it renders. The team edit
            // itself did succeed; only this follow-up write failed, so stay
            // on the form with the error visible instead.
            setPrimaryTeamError(result.error);
            return;
          }
          remaining.shift();
        }
        setPendingPrimaryAssignments([]);
      }

      setSuccessMessage(
        'Team updated successfully. Changes will appear in the catalog within 30 seconds.',
      );
    } catch (err) {
      setServerError(String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (successMessage) {
    return (
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          {successMessage}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(`/catalog/default/group/${machineId}`)}
        >
          View Team
        </Button>
      </Box>
    );
  }

  return (
    <Box maxWidth={720} mx="auto">
      {deepLinkWarning && (
        <Box mb={3}>
          <Alert
            severity="warning"
            onClose={() => setDeepLinkWarning(undefined)}
          >
            {deepLinkWarning}
          </Alert>
        </Box>
      )}
      {deepLinkInfo && (
        <Box mb={3}>
          <Alert severity="info" onClose={() => setDeepLinkInfo(undefined)}>
            {deepLinkInfo}
          </Alert>
        </Box>
      )}
      {preAdded && (
        <Box mb={3}>
          <Alert severity="info">
            {preAdded.name} was pre-added from Slack — click Save to confirm.
          </Alert>
        </Box>
      )}

      <Box mb={3}>
        <MachineIdDisplay
          machineId={machineId}
          showPermanenceWarning={false}
          levelLabel={levelLabel}
        />
      </Box>

      <Box mb={3}>
        <TeamNameField
          value={teamName}
          onChange={setTeamName}
          excludeMachineId={machineId}
          showMachineIdPreview={false}
          // Keep the field label and the Machine ID tooltip naming the same
          // thing — a tooltip citing "Function Name" above a field labelled
          // "Team Name" would be its own inconsistency.
          label={`${levelLabel} Name`}
        />
      </Box>

      <Box mb={3}>
        <ParentGroupPicker
          value={parent}
          onChange={handleParentChange}
          excludeMachineId={machineId}
          compact
        />
      </Box>

      {/* Mission Statement — FR-016/017/018/019 */}
      <Box mb={3}>
        <TextField
          fullWidth
          label="Mission Statement"
          multiline
          rows={4}
          variant="outlined"
          value={mission}
          onChange={e => setMission(e.target.value)}
          onFocus={() => setMissionFocused(true)}
          onBlur={() => setMissionFocused(false)}
          helperText={
            missionFocused
              ? `${mission.length}/${MAX_MISSION_LENGTH} characters (optional)`
              : undefined
          }
          inputProps={{ maxLength: MAX_MISSION_LENGTH + 10 }}
        />
      </Box>

      <Box mb={3}>
        <FourInTheBoxSection
          value={leadership}
          onChange={handleLeadershipChange}
          onMemberAdded={handleMemberAdded}
          onMemberRemoved={handleMemberRemoved}
          renderMemberAction={renderPrimaryStarAction}
        />
      </Box>

      {/* Members — FR-032/033: leadership refs excluded from display */}
      <Box mb={3}>
        <MembersPicker
          value={members}
          highlightRef={preAdded?.ref}
          onChange={refs => {
            setMembers(refs);
            const refSet = new Set(refs);
            // The pre-add is only worth confirming while that row is still
            // in the list; removing it leaves nothing unsaved to highlight.
            setPreAdded(prev =>
              prev && !refSet.has(prev.ref) ? undefined : prev,
            );
            setMemberTitles(prev =>
              Object.fromEntries(
                Object.entries(prev).filter(([ref]) => refSet.has(ref)),
              ),
            );
          }}
          excludeRefs={Object.values(leadership).filter((r): r is string =>
            Boolean(r),
          )}
          onMemberAdded={handleMemberAdded}
          onMemberRemoved={handleMemberRemoved}
          renderMemberAction={ref => (
            <>
              {renderMemberTagAction(ref)}
              {renderPrimaryStarAction(ref)}
            </>
          )}
        />
        {primaryTeamError && (
          <Typography variant="caption" color="error">
            {primaryTeamError}
          </Typography>
        )}
      </Box>

      <Box mb={3}>
        <ResourceLinkEditor value={links} onChange={setLinks} />
      </Box>

      {isTeamLevel && (
        <Box mb={3}>
          <OwnsAssetsToggle
            value={ownsAssets}
            onChange={setOwnsAssets}
            machineId={machineId}
          />
        </Box>
      )}

      <Box mb={3}>
        <WorksOnBehalfOfPicker
          value={worksOnBehalfOf}
          onChange={setWorksOnBehalfOf}
          excludeMachineId={machineId}
        />
        <WoboInheritanceNotes
          context={woboContext}
          hasValue={Boolean(worksOnBehalfOf)}
          hadValue={Boolean(originalWobo)}
        />
      </Box>

      {serverError && (
        <Box mb={2}>
          <Typography color="error">{serverError}</Typography>
        </Box>
      )}

      <Box className={classes.actions}>
        {/* span below: a disabled button emits no pointer events for the tooltip */}
        <Tooltip
          title={deepLinkPending ? 'Looking up the member from the link…' : ''}
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              // A team can't be ownerless — at least one of EM or PM is required
              // (server-enforced; this is just a convenience check to avoid a
              // late 400).
              disabled={
                submitting ||
                deepLinkPending ||
                !(leadership.lead || leadership.pm) ||
                hasResourceLinkErrors(links)
              }
              startIcon={
                submitting ? <CircularProgress size={16} /> : undefined
              }
            >
              {submitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          onClick={() => navigate(`/catalog/default/group/${machineId}`)}
          disabled={submitting}
        >
          Cancel
        </Button>
      </Box>

      {/* Parent change warning dialog (FR-027) */}
      <Dialog
        open={showParentWarning}
        onClose={() => setShowParentWarning(false)}
      >
        <DialogTitle>Change Parent Group?</DialogTitle>
        <DialogContent>
          <Typography>
            Changing a team's parent group will move it in the organisational
            hierarchy. This affects reporting lines and access inheritance. Are
            you sure?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowParentWarning(false)}>Cancel</Button>
          <Button color="primary" onClick={confirmParentChange}>
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* Primary-team prompt on add (PAT-779) */}
      <Dialog open={Boolean(primaryPrompt)} onClose={() => {}}>
        <DialogTitle>
          Set primary team for {primaryPrompt?.memberDisplayName}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {primaryPrompt?.memberDisplayName} is now a member of multiple
            teams. Choose which one is their primary team.
          </Typography>
          <RadioGroup
            value={primaryPrompt?.selected ?? ''}
            onChange={e =>
              setPrimaryPrompt(prev =>
                prev ? { ...prev, selected: e.target.value } : prev,
              )
            }
          >
            {primaryPrompt?.teams.map(team => {
              const isCurrent =
                team.ref !== thisTeamRef &&
                team.ref === primaryPrompt.currentPrimaryRef;
              return (
                <FormControlLabel
                  key={team.ref}
                  value={team.ref}
                  control={<Radio />}
                  label={isCurrent ? `${team.title} (current)` : team.title}
                />
              );
            })}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={confirmPrimaryPrompt}
            disabled={!primaryPrompt?.selected}
          >
            Set as primary
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mandatory primary-team reassignment on removal — no skip/dismiss:
          this team was the removed person's primary, and they still have
          2+ other teams, so a replacement must be chosen immediately. */}
      <Dialog open={Boolean(removalPrimaryPrompt)} onClose={() => {}}>
        <DialogTitle>
          Choose a new primary team for{' '}
          {removalPrimaryPrompt?.memberDisplayName}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {removalPrimaryPrompt?.memberDisplayName}'s primary team was this
            team. Since they still belong to other teams, choose which one is
            their primary team now.
          </Typography>
          <RadioGroup
            value={removalPrimaryPrompt?.selected ?? ''}
            onChange={e =>
              setRemovalPrimaryPrompt(prev =>
                prev ? { ...prev, selected: e.target.value } : prev,
              )
            }
          >
            {removalPrimaryPrompt?.teams.map(team => (
              <FormControlLabel
                key={team.ref}
                value={team.ref}
                control={<Radio />}
                label={team.title}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={confirmRemovalPrimaryPrompt}
            disabled={!removalPrimaryPrompt?.selected}
          >
            Set as primary
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
